// Origin-aware back navigation for the top-bar back button.
//
// React Router keeps its position in the browser's session history stack in
// `window.history.state.idx`. We mirror the pathname of every location the
// SPA visits into a sessionStorage journal indexed by that idx. The back
// button walks the journal backwards from the current position to the
// nearest entry belonging to a different "place" (see navigationGroup) and
// jumps straight to it with navigate(delta) — a genuine history POP, so the
// target page gets its own history entry back (scroll memory, router state).
// With no in-app history to return to — external entry, bookmark, cleared
// session — it falls back to the front page, which was the button's entire
// behavior before this module existed.

import {useEffect} from "react";
import {
    matchPath,
    NavigationType,
    useLocation,
    useNavigate,
    useNavigationType,
} from "react-router-dom";
import {ALL_TOPIC_TYPES} from "../paths";

const JOURNAL_KEY = "kai:navigationJournal";

/**
 * The "place" a pathname belongs to for back-button purposes. Every URL
 * within one topic — segment routes, dokument sub-views, checklist fillings,
 * previews — shares the topic's group, so backing out of a topic never steps
 * through intra-topic history. Everything else groups by its own pathname.
 */
export function navigationGroup(pathname: string): string {
    const preview = matchPath(
        {path: "/preview/:topicId", end: false},
        pathname
    );
    if (preview) return `topic:${preview.params.topicId}`;

    const topic = matchPath(
        {path: "/:topicType/:topicName/:topicId", end: false},
        pathname
    );
    if (topic && ALL_TOPIC_TYPES.has(topic.params.topicType!.toLowerCase())) {
        return `topic:${topic.params.topicId}`;
    }

    return pathname;
}

// Sparse slots (history entries created before the journal existed, or after
// sessionStorage loss) round-trip through JSON as null; both read as "unknown".
type Journal = (string | null | undefined)[];

function readJournal(): Journal {
    try {
        const raw = sessionStorage.getItem(JOURNAL_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? (parsed as Journal) : [];
    } catch {
        return [];
    }
}

function writeJournal(journal: Journal) {
    try {
        sessionStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));
    } catch {
        // Best effort — without a journal the back button falls back to "/".
    }
}

function currentHistoryIndex(): number {
    return (window.history.state?.idx as number | undefined) ?? 0;
}

export function recordNavigation(
    idx: number,
    pathname: string,
    action: NavigationType
) {
    const journal = readJournal();
    journal[idx] = pathname;
    // A push discards the browser's forward entries; drop our mirror of them.
    // A replace overwrites in place and a pop just (re)records — forward
    // entries stay valid in both cases.
    if (action === NavigationType.Push) journal.length = idx + 1;
    writeJournal(journal);
}

/**
 * History delta (negative) from the entry at `idx` to the nearest previous
 * entry outside the current location's group, or null when there is none —
 * no in-app history below us, or a journal gap we must not jump into blindly.
 */
export function backTargetDelta(idx: number, pathname: string): number | null {
    const journal = readJournal();
    const group = navigationGroup(pathname);
    for (let j = idx - 1; j >= 0; j--) {
        const entry = journal[j];
        if (entry == null) return null;
        if (navigationGroup(entry) !== group) return j - idx;
    }
    return null;
}

/** Mount once inside the router so every visited location is journaled. */
export function useNavigationJournal() {
    const location = useLocation();
    const navigationType = useNavigationType();
    useEffect(() => {
        recordNavigation(
            currentHistoryIndex(),
            location.pathname,
            navigationType
        );
    }, [location, navigationType]);
}

/**
 * Click handler for the top-bar back button: return to the nearest previous
 * place in in-app history, or to the front page when there is none.
 */
export function useBackNavigation() {
    const navigate = useNavigate();
    const location = useLocation();
    return () => {
        const delta = backTargetDelta(
            currentHistoryIndex(),
            location.pathname
        );
        if (delta === null) {
            navigate("/");
        } else {
            navigate(delta);
        }
    };
}
